import { useEffect, useState } from 'react';

import { commands, events, type RecordingElapsedPayload } from '@/lib/platform/generated/bindings.gen';
import { computeElapsedMs } from '@/lib/recordings/elapsed';

/**
 * Authoritative elapsed recording time (ms, paused spans excluded), computed
 * LOCALLY from a registry-authoritative anchor and ticked every second.
 *
 * Why an anchor (not a pushed integer): a 1s broadcast of a pre-computed integer
 * is up to 1s stale on arrival and frozen between ticks, so surfaces lag and
 * drift out of sync. Instead the registry supplies the anchor (startedAtMs +
 * accumulatedPausedMs + pausedAtMs) — which only changes on pause/resume — and
 * every surface computes `now - startedAt - paused` on its own 1s tick. Result:
 * all surfaces ms-synced (same anchor) AND smooth (local tick).
 *
 * Two halves: PULL the anchor on mount (recording_get_elapsed) so a surface that
 * mounts mid-recording is correct immediately; PUSH anchor updates via
 * recording://elapsed (fired at start + each pause/resume). Returns null only
 * when there's no active recording for the id.
 */
export function useRecordingElapsed(recordingId: string): number | null {
  const [anchor, setAnchor] = useState<RecordingElapsedPayload | null>(null);
  // A local clock that advances once a second so the elapsed recomputes while
  // running. Paused recordings don't need it (elapsed is frozen) but a 1s tick
  // is cheap and keeps the code simple.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let active = true;
    let unlisten: (() => void) | undefined;
    setAnchor(null); // reset when the id changes

    if (recordingId) {
      commands.recordingGetElapsed(recordingId).then((res) => {
        if (!active) return;
        if (res.status === 'ok' && res.data && res.data.recordingId === recordingId) {
          setAnchor(res.data);
        }
      });
    }

    events.recordingElapsed
      .listen((e) => {
        if (e.payload.recordingId !== recordingId) return;
        setAnchor(e.payload);
      })
      .then((u) => {
        if (active) unlisten = u;
        else u();
      });

    return () => {
      active = false;
      unlisten?.();
    };
  }, [recordingId]);

  // Tick the local clock every second while there's a running anchor (paused →
  // elapsed is frozen, so no need to re-render).
  useEffect(() => {
    if (!anchor || anchor.pausedAtMs !== null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [anchor]);

  if (!anchor) return null;
  return computeElapsedMs({
    startedAt: anchor.startedAtMs,
    now,
    pausedAt: anchor.pausedAtMs,
    accumulatedPausedMs: anchor.accumulatedPausedMs,
  });
}
